import type { IAccountDataResponse, ITransaction } from '@hit-game/shared/types';
import { Loader2 } from 'lucide-react';

import { useSyncedHistory } from '../../hooks/useSyncedHistory';
import { accountService } from '../../services/account.service';
import { transactionService } from '../../services/betlog.service';
import { formatDateTime, formatMoney } from '../../utils/format';
import { Modal } from '../common/Modal';
import { SyncingBadge } from './SyncingBadge';

const PAGE_SIZE = 30;

/** `slipType` của paygate. */
const SLIP_DEPOSIT = 1;

interface TransactionModalProps {
  account: IAccountDataResponse | null;
  onClose: () => void;
}

/**
 * Lịch sử nạp/rút — mở ra là hiện bản trong DB ngay, rồi tự kéo bản mới về.
 *
 * Bản trong DB là bản DUY NHẤT còn tồn tại sau một giờ (nhà cái tự xoá theo
 * `inv_retention_time`), nên đồng bộ hỏng thì bảng giữ nguyên, không trống đi.
 */
export function TransactionModal({ account, onClose }: TransactionModalProps) {
  const accountId = account ? String(account._id) : null;

  const { items, loading, syncing } = useSyncedHistory<ITransaction>({
    accountId,
    load: async (id) => (await transactionService.list(id, { page: 1, limit: PAGE_SIZE })).items,
    sync: (id) => accountService.syncTransactions(id),
  });

  return (
    <Modal
      open={account !== null}
      onClose={onClose}
      maxWidth={760}
      title={
        <span className="flex items-center gap-2">
          Lịch sử nạp/rút
          <span className="text-ink-dim text-[12px] font-normal">{account?.username}</span>
          <SyncingBadge show={syncing} />
        </span>
      }
    >
      {loading ? (
        <div className="text-ink-dim flex items-center justify-center gap-2 py-10 text-[13px]">
          <Loader2 size={16} className="animate-spin" />
          Đang tải...
        </div>
      ) : items.length === 0 ? (
        <div className="text-ink-dim space-y-2 py-10 text-center text-[13px]">
          <p className="font-semibold">Chưa có giao dịch nào</p>
          <p className="text-ink-faint text-[11px]">
            {syncing
              ? 'Đang kéo dữ liệu từ nhà cái...'
              : 'Nhà cái chỉ ghi lịch sử giao dịch khi tài khoản đã đăng nhập qua tool (tool tự bật lưu 1 giờ sau mỗi lần đăng nhập). Lệnh nạp/rút trước đó không lấy lại được.'}
          </p>
        </div>
      ) : (
        <div className="max-h-[55vh] overflow-y-auto">
          <table className="w-full border-collapse text-left">
            <thead className="bg-canvas-raised sticky top-0">
              <tr>
                {['Thời gian', 'Loại', 'Số tiền', 'Coin / Mạng', 'Trạng thái'].map((column) => (
                  <th
                    key={column}
                    className="text-ink-faint px-3 py-2 text-[10px] font-bold uppercase tracking-[0.1em]"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const isDeposit = item.slipType === SLIP_DEPOSIT;
                return (
                  <tr
                    key={item.transactionId}
                    className="border-line hover:bg-panel-hover border-t"
                  >
                    <td className="text-ink-muted whitespace-nowrap px-3 py-2 font-mono text-[11px]">
                      {formatDateTime(item.requestTime)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`rounded px-2 py-0.5 text-[10px] font-bold ${
                          isDeposit ? 'text-success bg-success/10' : 'text-warning bg-warning/10'
                        }`}
                      >
                        {isDeposit ? 'Nạp' : 'Rút'}
                      </span>
                    </td>
                    <td className="text-ink px-3 py-2 text-right font-mono text-[12px] font-bold">
                      {formatMoney(item.amount)}
                    </td>
                    <td className="text-ink-muted px-3 py-2 text-[11px]">
                      {item.currency ?? '—'}
                      {item.network ? ` / ${item.network}` : ''}
                    </td>
                    <td className="text-ink-dim px-3 py-2 text-[11px]">
                      {item.statusDescription || String(item.status)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
